import React from 'react';

import { client } from '@oysterjs/ais/api/client';
import {
  CaraWorkspaceMessageExportFormat,
  ExportCaraWorkspaceMessageDocument,
  type ExportCaraWorkspaceMessageMutation,
  type ExportCaraWorkspaceMessageMutationVariables
} from '@oysterjs/ais/api/graphql';
import { downloadBase64File, downloadFileFromUrl } from '@oysterjs/ais/lib/download';

const EXPORT_ERROR_MESSAGE = "Couldn't create the download. Try again.";

/**
 * Owns the ephemeral Word/Excel/PDF export mutation for a single agent message.
 * Uses a ref-based reentrancy guard so rapid double-clicks cannot issue
 * duplicate export requests.
 *
 * Word and Excel come back as inline bytes. PDF comes back as a single-use URL
 * because only the statics service can run the headless browser that renders it,
 * so that format is fetched from the URL and saved from the response.
 */
export function useExportCaraWorkspaceMessage(args: { workspaceId: string; messageId: string }) {
  const { workspaceId, messageId } = args;
  const [exportingFormat, setExportingFormat] =
    React.useState<CaraWorkspaceMessageExportFormat | null>(null);
  const [exportError, setExportError] = React.useState<string | null>(null);
  const exportingRef = React.useRef(false);

  const exportMessage = React.useCallback(
    async (format: CaraWorkspaceMessageExportFormat) => {
      if (exportingRef.current) return;
      exportingRef.current = true;
      setExportError(null);
      setExportingFormat(format);

      try {
        const result = await client.mutate<
          ExportCaraWorkspaceMessageMutation,
          ExportCaraWorkspaceMessageMutationVariables
        >({
          mutation: ExportCaraWorkspaceMessageDocument,
          variables: {
            input: {
              workspaceId,
              messageId,
              format
            }
          },
          fetchPolicy: 'no-cache'
        });

        if (result.errors && result.errors.length > 0) {
          setExportError(EXPORT_ERROR_MESSAGE);
          return;
        }

        const payload = result.data?.exportCaraWorkspaceMessage;
        if (!payload?.fileName || !payload.mimeType) {
          setExportError(EXPORT_ERROR_MESSAGE);
          return;
        }

        if (payload.downloadUrl) {
          await downloadFileFromUrl(payload.downloadUrl, payload.fileName);
          return;
        }

        if (!payload.contentBase64) {
          setExportError(EXPORT_ERROR_MESSAGE);
          return;
        }

        downloadBase64File(payload.contentBase64, payload.fileName, payload.mimeType);
      } catch {
        setExportError(EXPORT_ERROR_MESSAGE);
      } finally {
        exportingRef.current = false;
        setExportingFormat(null);
      }
    },
    [workspaceId, messageId]
  );

  return {
    exportingFormat,
    exportError,
    exportMessage,
    clearExportError: () => setExportError(null)
  };
}
